/**
 * Payment-intents list.
 *
 * URL is the source of truth for filters and page so the view is
 * shareable + refresh-stable. Search debounces 250ms before syncing to
 * the URL. The hero's tiles are the main State filter. Rows and counts
 * refresh every 30 s while the tab is visible. Row click opens a
 * quick-look Sheet; "Open full page" navigates to the canonical detail.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownLeft,
  ArrowUpRight,
  ChevronRight,
  Inbox,
  RotateCw,
  Send,
  Undo2,
  X,
  type LucideIcon,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SelectNative } from "@/components/ui/select-native";
import { DataTable, type DataTableColumn } from "@/components/shell/DataTable";
import { EmptyState } from "@/components/shell/EmptyState";
import { PageHeader } from "@/components/shell/PageHeader";
import { NameAvatar } from "@/components/shell/NameAvatar";
import { CopyId } from "@/components/shell/CopyId";
import { ListPagination } from "@/components/shell/ListPagination";
import { RelativeTime } from "@/components/shell/RelativeTime";
import { SearchInput } from "@/components/shell/SearchInput";
import { SegmentedControl } from "@/components/shell/SegmentedControl";
import { Toolbar } from "@/components/shell/Toolbar";
import { IntentStateTiles, TILE_STATES } from "@/components/payments/IntentStateTiles";
import { PaymentIntentSheet } from "@/components/payments/PaymentIntentSheet";
import { StateBadge } from "@/components/payments/StateBadge";
import {
  getPaymentIntents,
  type IntentDirection,
  type IntentState,
  type PaymentIntentRow,
} from "@/lib/api";
import { useAuth } from "@/lib/use-auth";
import { useEnvironment } from "@/lib/environment-context";
import { isMissingPage, LIST_PAGE_SIZE, usePageParam } from "@/lib/use-page-param";
import { formatAge, formatMethod, formatMinorUnits } from "@/lib/format";
import { cn } from "@/lib/utils";

const STATES: readonly IntentState[] = [
  "requires_action",
  "processing",
  "succeeded",
  "failed",
  "cancelled",
  "refunding",
  "refunded",
] as const;
const DIRECTIONS: readonly IntentDirection[] = ["collect", "payout", "refund"] as const;

/** States with no hero tile; the toolbar's select covers them. */
const OTHER_STATES = STATES.filter((s) => !TILE_STATES.has(s));

const REFRESH_MS = 30_000;
// Each tile is a full count on a table that only grows.
const COUNTS_REFRESH_MS = 120_000;
/** Show how long a payment has been processing once it's this old... */
const SHOW_AGE_AFTER_S = 5 * 60;
/** ...and flag it from here, the same 30 min the payout alerts page at. */
const STUCK_AFTER_S = 30 * 60;

const DIRECTION_STYLE: Readonly<Record<IntentDirection, { icon: LucideIcon; tone: string }>> = {
  payout: { icon: ArrowUpRight, tone: "text-info" },
  collect: { icon: ArrowDownLeft, tone: "text-success" },
  refund: { icon: Undo2, tone: "text-warning" },
};

function processingAgeS(row: PaymentIntentRow): number | null {
  if (row.state !== "processing") return null;
  const then = new Date(row.created_at).getTime();
  return Number.isNaN(then) ? null : Math.floor((Date.now() - then) / 1000);
}

export function PaymentIntentsListPage() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();

  const { environment: env } = useEnvironment();
  const status = (params.get("status") as IntentState | null) ?? undefined;
  const direction = (params.get("direction") as IntentDirection | null) ?? undefined;
  const q = params.get("q") ?? undefined;
  const { page, setPage } = usePageParam();
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState(q ?? "");
  useEffect(() => {
    const t = setTimeout(() => {
      // Skips the mount run too, so a shared ?page=3 link keeps its page.
      if (searchInput === (params.get("q") ?? "")) return;
      const next = new URLSearchParams(params);
      if (searchInput) next.set("q", searchInput);
      else next.delete("q");
      next.delete("page");
      setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["payment-intents", { env, status, direction, q, page }] as const,
    queryFn: () => getPaymentIntents({ environment: env, status, direction, q, page, page_size: LIST_PAGE_SIZE }),
    refetchInterval: REFRESH_MS,
    // Keep the rows on screen while the next filter or page loads.
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.error && !isMissingPage(query.error, page)) {
      toast.error("Couldn't load payment intents", {
        description: "Check your network and try again.",
      });
    }
  }, [query.error, page]);

  const [sheetId, setSheetId] = useState<string | null>(null);

  function setFilter(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  function refresh() {
    void query.refetch();
    void queryClient.invalidateQueries({ queryKey: ["payment-intents", "count"] });
  }

  function clearFilters() {
    setSearchInput("");
    setParams(new URLSearchParams(), { replace: true });
  }

  const hasFilters = Boolean(status || direction || q);
  const isZeroGrantTenant =
    user?.role === "tenant" && user.tenants.length === 0 && !hasFilters;

  const columns = useMemo<ReadonlyArray<DataTableColumn<PaymentIntentRow>>>(
    () => [
      {
        key: "payment",
        header: "Payment",
        cell: (r) => {
          const { icon: Icon, tone } = DIRECTION_STYLE[r.direction];
          return (
            <div className="flex min-w-0 items-center gap-3">
              <NameAvatar name={r.tenant_name} className="hidden sm:flex" />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="max-w-36 truncate font-medium sm:max-w-none">{r.tenant_name}</span>
                  {/* Live is the normal case; only test money gets a label. */}
                  {r.tenant_environment === "test" ? <Badge variant="muted">Test</Badge> : null}
                </div>
                <div className="flex max-w-40 items-center gap-1 text-xs text-muted-foreground sm:max-w-none">
                  <Icon className={cn("h-3 w-3 shrink-0", tone)} aria-hidden />
                  <span className="capitalize">{r.direction}</span>
                  <span aria-hidden>·</span>
                  <span className="truncate">{formatMethod(r.method)}</span>
                </div>
                <div className="mt-1 sm:hidden">
                  <StateBadge state={r.state} />
                </div>
              </div>
            </div>
          );
        },
      },
      {
        key: "amount",
        header: "Amount",
        className: "text-right",
        cell: (r) => (
          <span className="whitespace-nowrap font-semibold tabular-nums">
            {formatMinorUnits(r.amount, r.currency)}
          </span>
        ),
      },
      {
        key: "state",
        header: "State",
        className: "hidden sm:table-cell",
        cell: (r) => {
          const age = processingAgeS(r);
          return (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <StateBadge state={r.state} />
              {age !== null && age >= SHOW_AGE_AFTER_S ? (
                <span
                  title={`Processing for ${formatAge(age)}`}
                  className={cn(
                    "font-mono text-xs",
                    age >= STUCK_AFTER_S ? "font-semibold text-warning" : "text-muted-foreground",
                  )}
                >
                  {formatAge(age)}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        key: "id",
        header: "Intent",
        className: "hidden md:table-cell",
        cell: (r) => (
          <CopyId id={r.id} />
        ),
      },
      {
        key: "created_at",
        header: "Created",
        className: "hidden text-muted-foreground md:table-cell",
        cell: (r) => (
          <span className="flex items-center justify-between gap-2">
            <RelativeTime iso={r.created_at} />
            <ChevronRight
              className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-60"
              aria-hidden
            />
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        icon={Send}
        title="Payment intents"
        description="Collects, payouts and refunds across every tenant you can see."
        action={
          <>
            <span
              className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"
              title={
                query.dataUpdatedAt
                  ? `Last updated ${new Date(query.dataUpdatedAt).toLocaleTimeString()}. Refreshes every 30 seconds.`
                  : undefined
              }
            >
              <span aria-hidden className="relative flex h-2 w-2">
                {query.isError ? null : (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                )}
                <span
                  className={cn(
                    "relative inline-flex h-2 w-2 rounded-full",
                    query.isError ? "bg-rose-500" : "bg-emerald-500",
                  )}
                />
              </span>
              {query.isError ? "Retrying" : "Auto-refresh"}
            </span>
            <Button variant="outline" size="sm" onClick={refresh} disabled={query.isFetching}>
              <RotateCw className={query.isFetching ? "animate-spin" : undefined} />
              Refresh
            </Button>
          </>
        }
      >
        <IntentStateTiles
          filters={{ environment: env, direction, q }}
          active={status}
          onSelect={(s) => setFilter("status", s)}
          refetchInterval={COUNTS_REFRESH_MS}
        />
      </PageHeader>

      <Toolbar
        search={
          <SearchInput
            placeholder="Search by intent ID, key or provider ref…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onClear={() => setSearchInput("")}
          />
        }
        actions={
          <>
            <SegmentedControl
              label="Direction"
              value={direction}
              options={DIRECTIONS}
              onChange={(v) => setFilter("direction", v)}
            />
            <SelectNative
              aria-label="Other states"
              className="h-9 w-40 text-xs capitalize"
              value={status && !TILE_STATES.has(status) ? status : ""}
              onChange={(e) => setFilter("status", e.target.value || null)}
            >
              <option value="">Other states…</option>
              {OTHER_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </SelectNative>
            {hasFilters ? (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X />
                Clear filters
              </Button>
            ) : null}
          </>
        }
      />

      <DataTable<PaymentIntentRow>
        stale={query.isPlaceholderData}
        columns={columns}
        rows={query.data?.results ?? []}
        loading={query.isLoading}
        rowKey={(r) => r.id}
        selectedKey={sheetId ?? undefined}
        onRowClick={(r) => setSheetId(r.id)}
        rowClassName={(r) => {
          const age = processingAgeS(r);
          return age !== null && age >= STUCK_AFTER_S ? "bg-warning/5" : undefined;
        }}
        emptyState={
          isZeroGrantTenant ? (
            <EmptyState
              icon={Inbox}
              title="No tenants granted yet"
              description="Payment intents appear here once a super-admin grants you access to a tenant."
            />
          ) : (
            <EmptyState
              icon={Inbox}
              title="No payment intents match these filters"
              description="Try clearing a filter. New intents flow in here as they're created via the public API."
              action={
                hasFilters ? (
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />

      <ListPagination query={query} page={page} onPage={setPage} />

      <PaymentIntentSheet
        id={sheetId}
        open={sheetId !== null}
        onOpenChange={(open) => {
          if (!open) setSheetId(null);
        }}
      />
    </>
  );
}

export default PaymentIntentsListPage;
